import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Header,
  Button,
  Item,
  Pagination,
  Segment,
  Dimmer,
  Loader,
  Message,
  Icon,
  Dropdown
} from "semantic-ui-react";
import { searchYes24, searchKyobo, searchRidi } from "../api/searchApi";
import { formatPrice } from "../../../common/utils/format";

const SOURCE_LABEL = {
  yes24: "YES24 (일반도서)",
  kyobo: "교보문고 (전자책)",
  ridi: "리디북스 (전자책)"
};

const PAGE_SIZE_OPTIONS = [
  { key: "5", text: "5", value: 5 },
  { key: "10", text: "10", value: 10 },
  { key: "20", text: "20", value: 20 },
  { key: "40", text: "40", value: 40 },
  { key: "100", text: "100", value: 100 }
];

// 리디/교보는 외부 사이트가 페이지당 결과 수를 고정 반환함 (kyobo=20, ridi=24).
// 클라이언트 pageSize 가 다르면 백엔드 페이지 한 개~여러 개를 fetch 해서 slice 해 보여줌.
const BACKEND_PAGE_SIZE = { kyobo: 20, ridi: 24 };

// 출판일: "20251231" → "2025/12/31"
const formatPublishDate = s => {
  if (!s || s.length < 8) return s || "";
  return `${s.substring(0, 4)}/${s.substring(4, 6)}/${s.substring(6, 8)}`;
};

/**
 * 복수권 전용 도서 검색 모달.
 * source 에 따라 백엔드 검색 API 호출 후 정규화된 items 표시.
 * 항목 클릭 → onSelect({ bookName, reqUrl, price }) 콜백 → 부모 컴포넌트 폼에 자동 반영.
 *
 * 단일권 페이지의 모달과는 완전히 분리되어 있음 (redux/redux-form 의존 없음).
 */
const BookSearchModal = ({
  open,
  source, // "yes24" | "kyobo" | "ridi"
  initialKeyword,
  onSelect,
  onClose
}) => {
  const [keyword, setKeyword] = useState(initialKeyword || "");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // ridi/kyobo 백엔드 페이지 캐시. key = `${source}:${keyword}` 가 바뀌면 reset.
  // 이렇게 두면 사용자가 동일 검색어에서 페이지 사이즈만 5↔10↔20 바꿀 때
  // 같은 백엔드 페이지를 재호출하지 않고 메모리에서 slice 만 새로 함.
  const cacheRef = useRef({ key: null, pages: {} });

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleErr = useCallback(err => {
    setItems([]);
    setTotalCount(0);
    setErrorMessage(
      err && err.tokenExpired
        ? "세션이 만료되었습니다."
        : "검색에 실패했습니다."
    );
  }, []);

  // 백엔드 페이지 하나 fetch (캐시 hit 시 즉시 반환).
  const fetchBackendPage = useCallback(
    (kw, backendPage) => {
      const cacheKey = `${source}:${kw}`;
      if (cacheRef.current.key !== cacheKey) {
        cacheRef.current = { key: cacheKey, pages: {} };
      }
      const cached = cacheRef.current.pages[backendPage];
      if (cached) return Promise.resolve(cached);
      const fn = source === "kyobo" ? searchKyobo : searchRidi;
      return fn({ bookName: kw, page: backendPage }).then(res => {
        cacheRef.current.pages[backendPage] = res;
        return res;
      });
    },
    [source]
  );

  const search = useCallback(
    (kw, p, ps) => {
      if (!kw || !kw.trim()) {
        setItems([]);
        setTotalCount(0);
        return;
      }
      setLoading(true);
      setErrorMessage(null);

      // yes24 는 백엔드가 pageSize 를 그대로 받으므로 1 페이지 = 1 fetch.
      if (source === "yes24") {
        searchYes24({ bookName: kw, page: p - 1, pageSize: ps })
          .then(res => {
            setItems(res.items || []);
            setTotalCount(res.totalCount || 0);
          })
          .catch(handleErr)
          .finally(() => setLoading(false));
        return;
      }

      // ridi/kyobo: 백엔드 페이지 사이즈가 고정. 클라이언트 pageSize 만큼 slice.
      const bps = BACKEND_PAGE_SIZE[source];
      const startIdx = (p - 1) * ps;
      const endIdx = startIdx + ps; // exclusive
      const startBP = Math.floor(startIdx / bps) + 1;
      const endBP = Math.floor((endIdx - 1) / bps) + 1;
      const pages = [];
      for (let bp = startBP; bp <= endBP; bp++) pages.push(bp);

      Promise.all(pages.map(bp => fetchBackendPage(kw, bp)))
        .then(results => {
          const total =
            results[0] && results[0].totalCount ? results[0].totalCount : 0;
          // 백엔드 페이지들을 순서대로 이어붙임. baseOffset = 시작 백엔드 페이지의 시작 인덱스.
          const baseOffset = (startBP - 1) * bps;
          let combined = [];
          results.forEach((r, i) => {
            const bp = pages[i];
            const localOffset = (bp - 1) * bps - baseOffset;
            const arr = r.items || [];
            for (let j = 0; j < arr.length; j++) combined[localOffset + j] = arr[j];
          });
          const sliced = combined.slice(startIdx - baseOffset, endIdx - baseOffset);
          // hole(undefined) 제거 — 마지막 페이지 잘릴 때.
          setItems(sliced.filter(Boolean));
          setTotalCount(total);
        })
        .catch(handleErr)
        .finally(() => setLoading(false));
    },
    [source, fetchBackendPage, handleErr]
  );

  // 모달 열림 시 초기 검색
  useEffect(() => {
    if (open) {
      setKeyword(initialKeyword || "");
      setPage(1);
      setPageSize(5);
      setItems([]);
      setTotalCount(0);
      if (initialKeyword && initialKeyword.trim()) {
        search(initialKeyword, 1, 5);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source]);

  // 모바일 뒤로가기 / iOS Safari swipe-back 시 페이지를 떠나지 않고 모달만 닫히게.
  // 모달 열릴 때 dummy history entry 푸시 → popstate 발생하면 onClose 호출.
  // X / 항목 선택 / 외부 클릭 등 다른 경로로 닫히면 cleanup 에서 dummy entry 를
  // history.back() 으로 회수 (사용자가 다음에 뒤로가기 누르면 정상적으로 이전 페이지로).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ dbrsModal: true }, "");
    let consumedByPop = false;
    const onPopState = () => {
      consumedByPop = true;
      onClose();
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (!consumedByPop && window.history.state && window.history.state.dbrsModal) {
        window.history.back();
      }
    };
  }, [open, onClose]);

  const onSearch = useCallback(() => {
    setPage(1);
    search(keyword, 1, pageSize);
  }, [keyword, pageSize, search]);

  const onPaginationChange = useCallback(
    (e, { activePage }) => {
      setPage(activePage);
      search(keyword, activePage, pageSize);
    },
    [keyword, pageSize, search]
  );

  const onPageSizeChange = useCallback(
    (e, { value }) => {
      const ps = Number(value);
      setPageSize(ps);
      setPage(1);
      search(keyword, 1, ps);
    },
    [keyword, search]
  );

  const onPickItem = useCallback(
    item => {
      onSelect({
        bookName: item.bookName,
        reqUrl: item.reqUrl,
        price: item.price
      });
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <Modal closeIcon open={!!open} onClose={onClose} size="large">
      <Header icon="book" content={`도서 검색 — ${SOURCE_LABEL[source] || ""}`} />
      <Modal.Content>
        <Segment>
          <div style={{ display: "flex", gap: 8, marginBottom: "1em", alignItems: "center" }}>
            <input
              type="text"
              placeholder="도서명을 입력해주세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") onSearch();
              }}
              style={{ flex: 1, padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4 }}
              autoFocus
            />
            <Button primary onClick={onSearch}>
              <Icon name="search" /> 검색
            </Button>
            <Dropdown
              compact
              selection
              value={pageSize}
              onChange={onPageSizeChange}
              options={PAGE_SIZE_OPTIONS}
            />
          </div>

          {loading ? (
            <Dimmer inverted active>
              <Loader content="검색중..." />
            </Dimmer>
          ) : null}

          {errorMessage ? (
            <Message negative size="small">
              {errorMessage}
            </Message>
          ) : null}

          {items.length === 0 && !loading ? (
            <p style={{ color: "#999", textAlign: "center", padding: "2em 0" }}>
              {keyword ? "검색 결과가 없습니다." : "도서명을 입력하고 검색을 눌러주세요."}
            </p>
          ) : null}

          {items.length > 0 ? (
            <Item.Group divided>
              {items.map(item => (
                <Item key={item.id}>
                  {item.imageUrl ? (
                    <Item.Image src={item.imageUrl} size="small" />
                  ) : null}
                  <Item.Content>
                    <Item.Header>{item.bookName}</Item.Header>
                    <Item.Meta>
                      <div>
                        <b>금액 : </b>
                        <span>{formatPrice(item.price)}원</span>
                      </div>
                      {item.author ? (
                        <div>
                          <b>저자 : </b>
                          <span>{item.author}</span>
                        </div>
                      ) : null}
                      {item.publisher ? (
                        <div>
                          <b>출판사 : </b>
                          <span>{item.publisher}</span>
                        </div>
                      ) : null}
                      {item.publishDate ? (
                        <div>
                          <b>출판일 : </b>
                          <span>{formatPublishDate(item.publishDate)}</span>
                        </div>
                      ) : null}
                    </Item.Meta>
                    <Item.Extra>
                      <Button primary size="small" onClick={() => onPickItem(item)}>
                        <Icon name="check" /> 선택
                      </Button>
                      {item.reqUrl ? (
                        <Button
                          size="small"
                          as="a"
                          href={item.reqUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          상세 페이지
                        </Button>
                      ) : null}
                    </Item.Extra>
                  </Item.Content>
                </Item>
              ))}
            </Item.Group>
          ) : null}
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        {totalCount > 0 ? (
          <Pagination
            pointing
            secondary
            activePage={page}
            totalPages={totalPages}
            onPageChange={onPaginationChange}
            siblingRange={1}
            boundaryRange={1}
          />
        ) : null}
        <Button onClick={onClose}>닫기</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BookSearchModal;
